import { useEffect, useState } from 'react';
import { Mail, Loader2, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import type { Application, EmailThread } from '@core/types';
import { emailService } from '@core/supabase/services/emailService';
import { ListPreviewDrawer } from '@shared/components/ui/ListPreviewDrawer';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

function DeliveryChip({ thread }: { thread: EmailThread }) {
  if (thread.opened_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-700/40 dark:bg-emerald-900/20 dark:text-emerald-400">
        <CheckCircle className="h-3 w-3" /> Opened
      </span>
    );
  }
  if (thread.sent_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
        <CheckCircle className="h-3 w-3" /> Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-400">
      <Clock className="h-3 w-3" /> Draft
    </span>
  );
}

interface Props {
  applications: Application[];
}

export function CandidateEmailsTab({ applications }: Props) {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openThread, setOpenThread] = useState<EmailThread | null>(null);

  useEffect(() => {
    const appIds = applications.map((a) => a.id).filter(Boolean);
    if (appIds.length === 0) {
      setThreads([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all(appIds.map((id) => emailService.listByApplication(id)))
      .then((results) => {
        const all = results.flat();
        all.sort((a, b) => {
          const ta = a.sent_at ?? a.created_at ?? '';
          const tb = b.sent_at ?? b.created_at ?? '';
          return tb.localeCompare(ta);
        });
        setThreads(all);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load emails'))
      .finally(() => setLoading(false));
  }, [applications]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-[13px] vscode-text-tertiary">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading emails…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-8 text-[13px] text-red-500">
        <AlertCircle className="h-4 w-4" /> {error}
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <Mail className="h-8 w-8 vscode-text-tertiary opacity-40" />
        <p className="text-[13px] vscode-text-secondary">No emails for this candidate yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="divide-y divide-[var(--vscode-border-primary)]">
        {threads.map((t) => (
          <button
            key={t.id}
            type="button"
            className="flex w-full items-start gap-3 px-0 py-3 text-left transition-colors hover:bg-[var(--vscode-bg-hover)] rounded"
            onClick={() => setOpenThread(t)}
          >
            <Mail className="mt-0.5 h-4 w-4 shrink-0 vscode-text-tertiary" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-[13px] font-medium vscode-text-primary">{t.subject || '(No subject)'}</p>
                <DeliveryChip thread={t} />
              </div>
              <p className="truncate text-[12px] vscode-text-secondary">
                To: {t.recipient_name ? `${t.recipient_name} <${t.recipient_email}>` : t.recipient_email}
              </p>
              {t.sent_at ? (
                <p className="text-[11px] vscode-text-tertiary">{formatDateTimeGmt(t.sent_at)}</p>
              ) : null}
            </div>
          </button>
        ))}
      </div>

      <ListPreviewDrawer
        open={openThread != null}
        onClose={() => setOpenThread(null)}
        title={openThread?.subject || '(No subject)'}
        subtitle={openThread ? `To: ${openThread.recipient_email}` : undefined}
        typeLabel="Email"
      >
        {openThread ? (
          <div className="p-4">
            <div className="mb-4 space-y-1 text-[12px] vscode-text-secondary">
              <p><span className="font-medium">To:</span> {openThread.recipient_name ? `${openThread.recipient_name} <${openThread.recipient_email}>` : openThread.recipient_email}</p>
              {openThread.sent_at ? <p><span className="font-medium">Sent:</span> {formatDateTimeGmt(openThread.sent_at)}</p> : null}
            </div>
            {openThread.body_html ? (
              <div
                className="prose prose-sm max-w-none text-[13px] vscode-text-primary"
                dangerouslySetInnerHTML={{ __html: openThread.body_html }}
              />
            ) : (
              <p className="text-[13px] vscode-text-tertiary">No email body stored.</p>
            )}
          </div>
        ) : null}
      </ListPreviewDrawer>
    </>
  );
}
